/**
 * wayai migrate — bring a repo onto the current workspace shape.
 *
 * Two independent moves, both idempotent, either of which may already be done:
 *
 *   1. The layout:  workspace/<hub>/  →  wayai-ws/hubs/<hub>/
 *                   org/              →  wayai-ws/org/
 *   2. The org binding: `.wayai.yaml` at the root  →  `wayai-ws/wayai.yaml`
 *
 * The layout move uses `git mv` (preserving history) for tracked dirs, falling
 * back to a filesystem rename for untracked / partially-tracked dirs or non-git
 * repos. The root-level agent-context files (`AGENTS.md` / `CLAUDE.md`) stay at
 * the root and are untouched.
 *
 * Both are decided BEFORE either is applied, so a refusal can never follow a
 * `git mv` — see the pre-flight block.
 *
 * **The org binding is COPIED, not moved.** `.wayai.yaml` stays exactly where it
 * is: a teammate or a CI runner still on an older CLI requires it, and this
 * command runs in one checkout while the repo is shared. It remains a supported
 * read fallback (`lib/repo-config.ts`); removing the fallback and the file is a
 * later major, once every environment that runs a given repo reads the manifest.
 * The output says so — an unexplained leftover file reads as litter and gets
 * deleted, which is precisely the failure this window exists to avoid.
 */

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { findGitRoot } from '../lib/workspace.js';
import { WAYAI_LAYOUT, isDirectory } from '../lib/layout.js';
import { planOrgMigration, writeRepoConfig } from '../lib/repo-config.js';
import { ROOT_CONFIG_FILE, WORKSPACE_MANIFEST_LABEL } from '../lib/workspace-manifest.js';

/** True when at least one tracked file lives at/under `p`. */
function isTracked(gitRoot: string, p: string): boolean {
  try {
    execFileSync('git', ['ls-files', '--error-unmatch', '--', path.relative(gitRoot, p)], {
      cwd: gitRoot,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Move `from` → `to`, creating `to`'s parent. Prefers `git mv` for tracked
 * sources (history-preserving, staged); falls back to `fs.rename` when the
 * source is untracked or `git mv` errors (e.g. mixed tracked/untracked
 * contents). Returns which mechanism was used.
 */
function moveDir(gitRoot: string, from: string, to: string): 'git' | 'fs' {
  fs.mkdirSync(path.dirname(to), { recursive: true });

  if (isTracked(gitRoot, from)) {
    try {
      execFileSync('git', ['mv', path.relative(gitRoot, from), path.relative(gitRoot, to)], {
        cwd: gitRoot,
        stdio: ['pipe', 'pipe', 'pipe'],
      });
      return 'git';
    } catch {
      // Fall through — e.g. the dir mixes tracked and untracked children.
    }
  }

  fs.renameSync(from, to);
  return 'fs';
}

export async function migrateCommand(_args: string[]): Promise<void> {
  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not inside a git repository.');
    process.exit(1);
  }

  const rel = (p: string) => path.relative(gitRoot, p);

  const newWs = path.join(gitRoot, WAYAI_LAYOUT.wsDir);
  const legacyWs = path.join(gitRoot, WAYAI_LAYOUT.legacy.wsDir);
  const legacyOrg = path.join(gitRoot, WAYAI_LAYOUT.legacy.orgAtRoot);
  const newHubs = path.join(newWs, WAYAI_LAYOUT.hubsSubdir);
  const newOrg = path.join(newWs, WAYAI_LAYOUT.orgSubdir);

  const hasLegacyWs = isDirectory(legacyWs);
  const hasLegacyOrg = isDirectory(legacyOrg);

  // Validate EVERYTHING before moving ANYTHING — the two dir targets AND the org
  // binding. Any check left until after the moves would let `git mv` succeed and
  // stage a large rename, then abort non-zero, leaving a half-migrated repo that
  // a re-run can't cleanly recover and a caller reading exit 1 as "nothing
  // happened". `planOrgMigration` is a pure read, so it costs nothing here.
  const orgPlan = planOrgMigration(gitRoot);
  if (orgPlan.kind === 'refuse') {
    console.error(orgPlan.message);
    process.exit(1);
  }
  if (hasLegacyWs && isDirectory(newHubs)) {
    console.error(
      `Cannot migrate: target ${rel(newHubs)}/ already exists alongside legacy ${WAYAI_LAYOUT.legacy.wsDir}/. ` +
        `Merge them manually, then re-run \`wayai migrate\`.`,
    );
    process.exit(1);
  }
  if (hasLegacyOrg && isDirectory(newOrg)) {
    console.error(
      `Cannot migrate: target ${rel(newOrg)}/ already exists alongside legacy ${WAYAI_LAYOUT.legacy.orgAtRoot}/. ` +
        `Merge them manually, then re-run \`wayai migrate\`.`,
    );
    process.exit(1);
  }

  const moved: string[] = [];
  let usedFsFallback = false;

  if (hasLegacyWs) {
    const how = moveDir(gitRoot, legacyWs, newHubs);
    usedFsFallback ||= how === 'fs';
    moved.push(`${WAYAI_LAYOUT.legacy.wsDir}/ → ${rel(newHubs)}/`);
  }

  if (hasLegacyOrg) {
    const how = moveDir(gitRoot, legacyOrg, newOrg);
    usedFsFallback ||= how === 'fs';
    moved.push(`${WAYAI_LAYOUT.legacy.orgAtRoot}/ → ${rel(newOrg)}/`);
  }

  if (moved.length > 0) {
    console.log(`Migrated to the ${WAYAI_LAYOUT.wsDir}/ layout:`);
    for (const m of moved) console.log(`  ${m}`);
    if (usedFsFallback) {
      console.log('\n(Some dirs were moved on disk, not via git — they will show as untracked.)');
    }
  }

  // Applies whether or not there were legacy dirs: a repo already on the new
  // layout may still carry its org binding at the root. Decided in the
  // pre-flight above; only the write happens here.
  const copiedOrg = orgPlan.kind === 'copy';
  if (copiedOrg) {
    writeRepoConfig(orgPlan.config, gitRoot);
    console.log(`${moved.length > 0 ? '\n' : ''}Copied the organization binding into ${WORKSPACE_MANIFEST_LABEL}.`);
    console.log(
      `  ${ROOT_CONFIG_FILE} is KEPT on purpose — an older CLI in a teammate's environment or in CI still requires it.`,
    );
    console.log(
      '  It stays a supported fallback until the next major, which removes both the fallback and the file.',
    );
    console.log('  Delete it once every environment that runs this repo reads the manifest.');
  }

  if (moved.length === 0 && !copiedOrg) {
    console.log(
      `Already migrated — no legacy ${WAYAI_LAYOUT.legacy.wsDir}/ or ${WAYAI_LAYOUT.legacy.orgAtRoot}/ to move, ` +
        `and no organization binding to copy into ${WORKSPACE_MANIFEST_LABEL}.`,
    );
    return;
  }

  console.log('\nNext steps:');
  console.log('  git add -A && git commit -m "chore: migrate to wayai-ws layout"');
  console.log('  If your CI runs `wayai push`, commit this migration in the same change that updates CI.');
}
